/**
 * Reveal — wrapper que hace fade-up de su contenido cuando entra al viewport.
 *
 * Usa `IntersectionObserver`; desactivado automáticamente con
 * `prefers-reduced-motion`.
 *
 * Uso:
 *   <Reveal>...</Reveal>
 *   <Reveal delay={120} as="li">...</Reveal>
 */
import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';

import { useReducedMotion } from '../../hooks/useMediaQuery';
import { cn } from '../../utils/cn';

export interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: ElementType;
  /** Distancia inicial de translateY en px (default 24). */
  offset?: number;
}

export function Reveal({
  children,
  delay = 0,
  className,
  as: Tag = 'div',
  offset = 24,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <Tag
      ref={ref}
      className={cn(
        'transition-all duration-700 ease-out will-change-[opacity,transform]',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0',
        className,
      )}
      style={{
        transitionDelay: visible ? `${delay}ms` : '0ms',
        transform: visible ? undefined : `translateY(${offset}px)`,
      }}
    >
      {children}
    </Tag>
  );
}
